import { useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import { Hospital } from '../types';
import { changeHospitalStatus as apiChangeStatus, updateHospitalField as apiUpdateField, reprocessHospital as apiReprocess } from '../../../Http/Index';

const TOAST_DURATION = 3000;
const SUCCESS_TOAST_DURATION = 2000;

export const useHospitalActions = (
  hospitals: Hospital[],
  selectedMatches: Record<string, string>,
  setSelectedMatches: (fn: (prev: Record<string, string>) => Record<string, string>) => void,
  loadHospitals: () => Promise<void>,
  loadStats: () => Promise<void>
) => {
  const toast = useToast();

  const updateHospitalStatus = useCallback(async (id: string, status: Hospital['status']) => {
    const hospital = hospitals.find(h => h._id === id);
    const matchedId = selectedMatches[id];

    if ((hospital?.status === 'doubtful' || hospital?.status === 'new_hospital') && status === 'matched' && !matchedId) {
      toast({
        title: 'Error',
        description: 'Please select a hospital to match with before marking as matched',
        status: 'error',
        duration: TOAST_DURATION,
        isClosable: true,
      });
      return;
    }

    try {
      await apiChangeStatus(id, status, matchedId);

      if (matchedId) {
        setSelectedMatches(prev => {
          const newState = { ...prev };
          delete newState[id];
          return newState;
        });
      }

      await Promise.all([loadHospitals(), loadStats()]);

    } catch (error) {
      console.error('Failed to update hospital status:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update hospital status',
        status: 'error',
        duration: TOAST_DURATION,
        isClosable: true,
      });
    }
  }, [hospitals, selectedMatches, setSelectedMatches, loadHospitals, loadStats, toast]);

  const updateHospitalField = useCallback(async (id: string, fieldName: string, value: string) => {
    try {
      await apiUpdateField(id, fieldName, value);
      await loadHospitals();
    } catch (error) {
      console.error('Failed to update hospital field:', error);
    }
  }, [loadHospitals]);

  const reprocessHospital = useCallback(async (id: string) => {
    try {
      const response = await apiReprocess(id);
      await loadHospitals();
      await loadStats();
      toast({
        title: 'Success',
        description: response.data.message || 'Hospital queued for reprocessing',
        status: 'success',
        duration: SUCCESS_TOAST_DURATION,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to reprocess hospital:', error);
      toast({
        title: 'Error',
        description: 'Failed to reprocess hospital',
        status: 'error',
        duration: TOAST_DURATION,
        isClosable: true,
      });
    }
  }, [loadHospitals, loadStats, toast]);

  return {
    updateHospitalStatus,
    updateHospitalField,
    reprocessHospital
  };
};